import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingCompany, EFinancingProgram, ELanguage, ELoanPurpose, getELanguageList } from '@if/core/types'
import {
  ControlledCheckbox,
  ControlledPhoneField,
  ControlledSelect,
  ControlledTextField,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Cancel as CancelIcon, Send as SendIcon } from '@mui/icons-material'
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import AsyncActionButton from '@src/components/AsyncActionButton'
import DialogCloseButton from '@src/components/common/DialogCloseButton'
import { useSendInvitation } from '@src/data/api/invitation-api/invitation-api'
import { companyConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { ECommunicationType, InvitationFormData, invitationSchema } from '@src/types/InvitationSchema'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link as RouterDomLink } from 'react-router-dom'

interface Props {
  open: boolean
  onClose: () => void
  financingCompanyId?: EFinancingCompany
  loanPurposeId?: ELoanPurpose | null
}

const shouldShowField = (communicationType: ECommunicationType, fieldType: ECommunicationType): boolean => {
  if (fieldType === ECommunicationType.Email) {
    return communicationType === ECommunicationType.Email || communicationType === ECommunicationType.Both
  }
  if (fieldType === ECommunicationType.SMS) {
    return communicationType === ECommunicationType.SMS || communicationType === ECommunicationType.Both
  }
  return false
}

const InvitationDialog = ({ open, onClose, financingCompanyId, loanPurposeId }: Props) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation()
  const languageList = useTranslatedOptionEnum('ELanguage', getELanguageList(), 'enum')

  const isIceberg = financingCompanyId === EFinancingCompany.Iceberg
  const defaultFinancingProgram = isIceberg ? EFinancingProgram.MechanicalWarranty : EFinancingProgram.Personal

  const financingProgramList = useTranslatedOptionEnum(
    'EFinancingProgram',
    [EFinancingProgram.MechanicalWarranty],
    'enum',
  )
  const [sendInvitation, isPending, isError, resetMutation] = useSendInvitation()
  const [isSuccess, setIsSuccess] = useState(false)
  const [emailError, setEmailError] = useState<boolean>(false)
  const [smsError, setSmsError] = useState<boolean>(false)
  const [successTimeout, setSuccessTimeout] = useState<NodeJS.Timeout | null>(null)

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useForm<InvitationFormData>({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      languageId: ELanguage.French,
      communicationType: ECommunicationType.Both,
      email: '',
      mobilePhoneNumber: '',
      confirmationChecked: false,
      financingProgramId: defaultFinancingProgram,
    },
    resolver: yupResolver(invitationSchema),
  })

  const communicationType = watch('communicationType')
  const financingProgram = watch('financingProgramId')
  const handleFormSubmit = async (data: InvitationFormData) => {
    setIsSuccess(false)
    setEmailError(false)
    setSmsError(false)

    try {
      const result = await sendInvitation({
        firstName: data.firstName,
        lastName: data.lastName,
        languageId: data.languageId,
        communicationType: data.communicationType,
        email: shouldShowField(data.communicationType, ECommunicationType.Email) ? data.email || null : null,
        mobilePhoneNumber: shouldShowField(data.communicationType, ECommunicationType.SMS)
          ? data.mobilePhoneNumber || null
          : null,
        financingProgramId: data.financingProgramId,
        loanPurposeId: loanPurposeId ?? null,
      })

      let hasErrors = false

      if (
        result.emailFailed &&
        (data.communicationType === ECommunicationType.Email || data.communicationType === ECommunicationType.Both)
      ) {
        setEmailError(true)
        hasErrors = true
      }

      if (
        result.smsFailed &&
        (data.communicationType === ECommunicationType.SMS || data.communicationType === ECommunicationType.Both)
      ) {
        setSmsError(true)
        hasErrors = true
      }

      if (!hasErrors) {
        setIsSuccess(true)
        const timeoutId = setTimeout(() => {
          reset()
          onClose()
          setSuccessTimeout(null)
        }, 3000)
        setSuccessTimeout(timeoutId)
      }
    } catch (error) {
      console.error('Failed to send invitation:', error)
      const showEmailError =
        data.communicationType === ECommunicationType.Email || data.communicationType === ECommunicationType.Both
      const showSmsError =
        data.communicationType === ECommunicationType.SMS || data.communicationType === ECommunicationType.Both

      if (showEmailError) setEmailError(true)
      if (showSmsError) setSmsError(true)
    }
  }

  useEffect(() => {
    if (!open) {
      // Clear any pending timeout
      if (successTimeout) {
        clearTimeout(successTimeout)
        setSuccessTimeout(null)
      }
      reset()
      resetMutation()
      setIsSuccess(false)
      setEmailError(false)
      setSmsError(false)
    }
  }, [open, reset, resetMutation, successTimeout])

  const config = companyConfigByFinancingProgramId(financingProgram)
  const link = resolvedLanguage === 'fr' ? config.privacyPolicyLinkFR : config.privacyPolicyLinkEN

  return (
    <Dialog
      component="form"
      onSubmit={handleSubmit(handleFormSubmit)}
      open={open}
      onClose={onClose}
      aria-labelledby="email-sms-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="email-sms-dialog-title">
        {t('invitationDialog.title')}
        <DialogCloseButton onClose={onClose} />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {isIceberg && (
            <ControlledSelect
              control={control}
              name="financingProgramId"
              label={t('invitationDialog.financingProgram')}
              variant="outlined"
              fullWidth
              disabled
              options={financingProgramList}
              disableEmptyOption
            />
          )}
          <Grid container spacing={2}>
            <Grid size={6}>
              <ControlledTextField
                control={control}
                name="firstName"
                label={t('common.firstName')}
                variant="outlined"
                fullWidth
                required
              />
            </Grid>
            <Grid size={6}>
              <ControlledTextField
                control={control}
                name="lastName"
                label={t('common.lastName')}
                variant="outlined"
                fullWidth
                required
              />
            </Grid>
          </Grid>
          <ControlledSelect
            control={control}
            name="languageId"
            label={t('editCreditApplication.information.languageId')}
            variant="outlined"
            fullWidth
            options={languageList}
            disableEmptyOption
          />
          <ControlledSelect
            control={control}
            name="communicationType"
            label={t('invitationDialog.communicationType')}
            variant="outlined"
            fullWidth
            disableEmptyOption
            options={[
              <MenuItem key={ECommunicationType.Email} value={ECommunicationType.Email}>
                {t('invitationDialog.email')}
              </MenuItem>,
              <MenuItem key={ECommunicationType.SMS} value={ECommunicationType.SMS}>
                {t('invitationDialog.sms')}
              </MenuItem>,
              <MenuItem key={ECommunicationType.Both} value={ECommunicationType.Both}>
                {t('invitationDialog.emailAndSms')}
              </MenuItem>,
            ]}
          />
          {shouldShowField(communicationType, ECommunicationType.Email) && (
            <ControlledTextField
              control={control}
              name="email"
              label={t('invitationDialog.email')}
              variant="outlined"
              fullWidth
              type="email"
              required
            />
          )}
          {shouldShowField(communicationType, ECommunicationType.SMS) && (
            <ControlledPhoneField
              control={control}
              name="mobilePhoneNumber"
              label={t('common.phone')}
              variant="outlined"
              fullWidth
              required
            />
          )}
          <ControlledCheckbox
            control={control}
            name="confirmationChecked"
            label={
              <>
                {t('invitationDialog.consent', { company: isIceberg ? 'Iceberg Finance' : 'iFinance Canada' })}{' '}
                <Link component={RouterDomLink} to={link} target="_blank">
                  {t('incomeVerification.startVerification.termsAndConditions')}
                </Link>
              </>
            }
          />
          {(emailError || smsError) && (
            <Box sx={{ mt: 1 }}>
              {emailError && (
                <Typography variant="body1" color="error" display="block">
                  {t('invitationDialog.emailSendFailed')}
                </Typography>
              )}
              {smsError && (
                <Typography variant="body1" color="error" display="block">
                  {t('invitationDialog.smsSendFailed')}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <AsyncActionButton
          fullWidth={false}
          startIcon={<CancelIcon />}
          onClick={onClose}
          color="error"
          disabled={isPending}
        >
          {t('common.cancel')}
        </AsyncActionButton>
        <AsyncActionButton
          fullWidth={false}
          type="submit"
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
          disabled={!isValid}
          isPending={isPending}
          isError={isError}
          isSuccess={isSuccess}
        >
          {t('common.submit')}
        </AsyncActionButton>
      </DialogActions>
    </Dialog>
  )
}

export default InvitationDialog
